// src/pages/EnhancedDashboard.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CheckCircle, Clock, AlertTriangle, XCircle, Users, BookOpen,
  Calendar, Settings, TrendingUp, TrendingDown, ArrowRight,
  RefreshCw, Database, UserCheck, GraduationCap, Building,
  FileText, Grid3X3, Target, AlertCircle, ClipboardCheck,
  UserX, BarChart3
} from 'lucide-react';
import PageHeader from '../components/layout/PageHeader';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { useSimpleDashboardData } from '../hooks/useSimpleDashboardData';
import { useSetupWorkflow } from '../hooks/useSetupWorkflow';
import { useModulePermissions } from '../hooks/useModulePermissions';

const EnhancedDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { isSystemAdmin, hasAccess } = useModulePermissions();
  const { stats, alerts, loading, error, refresh } = useSimpleDashboardData();
  const { 
    setupSteps, 
    workflowData, 
    loading: setupLoading, 
    completeStep, 
    uncompleteStep, 
    getProgressStats 
  } = useSetupWorkflow();
  const [activeSection, setActiveSection] = useState<'overview' | 'workflow' | 'alerts'>('overview');

  // Handle URL query parameters
  useEffect(() => {
    const section = searchParams.get('section');
    if (section === 'workflow') {
      setActiveSection('workflow');
    }
  }, [searchParams]);

  // Status icon mapping
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-5 w-5 text-ok-fg" />;
      case 'in_progress': return <Clock className="h-5 w-5 text-navy-500" />;
      case 'needs_attention': return <AlertTriangle className="h-5 w-5 text-warn-fg" />;
      default: return <XCircle className="h-5 w-5 text-ink-400" />;
    }
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'error': return <XCircle className="h-5 w-5 text-danger-fg" />;
      case 'warning': return <AlertTriangle className="h-5 w-5 text-warn-fg" />;
      case 'info': return <AlertCircle className="h-5 w-5 text-navy-500" />;
      case 'success': return <CheckCircle className="h-5 w-5 text-ok-fg" />;
      default: return <AlertCircle className="h-5 w-5 text-ink-500" />;
    }
  };

  // Get progress from setup workflow
  const progressStats = getProgressStats;
  const overallProgress = progressStats?.progressPercentage || 0;
  const completedSteps = progressStats?.completedSteps || 0;
  const totalSteps = progressStats?.totalSteps || 0;

  // Priority actions for user's role
  const getPriorityActions = () => {
    if (!user?.role) return [];

    const roleActions: Record<string, Array<{name: string, icon: any, url: string, color: string}>> = {
      system_admin: [
        { name: 'System Setup', icon: Settings, url: '/admin/school-settings', color: 'bg-navy-500' },
        { name: 'User Management', icon: Users, url: '/settings/user-management', color: 'bg-navy-500' },
        { name: 'Import Grid', icon: Database, url: '/grid/import', color: 'bg-navy-500' },
        { name: 'View Reports', icon: TrendingUp, url: '/reports/preference-satisfaction', color: 'bg-navy-500' }
      ],
      admin: [ // Legacy compatibility
        { name: 'System Setup', icon: Settings, url: '/admin/school-settings', color: 'bg-navy-500' },
        { name: 'User Management', icon: Users, url: '/settings/user-management', color: 'bg-navy-500' },
        { name: 'Import Grid', icon: Database, url: '/grid/import', color: 'bg-navy-500' },
        { name: 'View Reports', icon: TrendingUp, url: '/reports/preference-satisfaction', color: 'bg-navy-500' }
      ],
      curriculum_leader: [
        { name: 'My Load Letter', icon: FileText, url: '/load-letter/staff-dashboard', color: 'bg-navy-500' },
        { name: 'Teacher Assignments', icon: BookOpen, url: '/curriculum/assignments', color: 'bg-navy-500' },
        { name: 'View Grid Assignments', icon: Grid3X3, url: '/curriculum/grids', color: 'bg-navy-500' }
      ],
      head_of_department: [
        { name: 'My Load Letter', icon: FileText, url: '/load-letter/staff-dashboard', color: 'bg-navy-500' },
        { name: 'Teacher Assignments', icon: BookOpen, url: '/curriculum/assignments', color: 'bg-navy-500' },
        { name: 'View Grid Assignments', icon: Grid3X3, url: '/curriculum/grids', color: 'bg-navy-500' }
      ],
      teacher: [
        { name: 'My Load Letter', icon: FileText, url: '/load-letter/staff-dashboard', color: 'bg-navy-500' },
        { name: 'Teaching Intentions', icon: Target, url: '/intentions', color: 'bg-navy-500' }
      ]
    };

    return roleActions[user.role] || roleActions.teacher;
  };

  if (loading || setupLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-ink-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-ink-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="p-6 text-center">
          <XCircle className="h-12 w-12 text-danger-fg mx-auto mb-4" />
          <h3 className="text-lg font-medium text-ink-900 mb-2">Dashboard Error</h3>
          <p className="text-ink-500 mb-4">{error}</p>
          <Button onClick={refresh} leftIcon={<RefreshCw className="h-4 w-4" />}>
            Try Again
          </Button>
        </Card>
      </div>
    );
  }

  // For non-admin users, show a simple welcome page
  if (!isSystemAdmin) {
    const roleBlurb: Record<string, string> = {
      curriculum_leader: 'Use the navigation menu to record your teacher preferences and review grid assignments for your curriculum area.',
      head_of_department: 'Use the navigation menu to record teacher preferences and review grid assignments across your curriculum areas.',
      assistant_principal: 'Use the navigation menu to manage curriculum preferences and review grid assignments.',
    };
    const blurb = user?.role ? roleBlurb[user.role] : undefined;
    return (
      <div className="space-y-6">
        <PageHeader
          title={`Welcome, ${user?.name || 'User'}`}
          description="Preferences, Intentions & Timetabling"
        />

        {/* Welcome Card */}
        <Card className="p-8 text-center">
          <div className="max-w-2xl mx-auto">
            <GraduationCap className="h-16 w-16 text-brand-500 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-ink-900 mb-4">
              Welcome to PIT
            </h2>
            <p className="text-lg text-ink-500 mb-6">
              Your central hub for teaching intentions, staff preferences and timetable planning.
            </p>

            {blurb && (
              <div className="bg-brand-50 border border-brand-200 rounded-lg p-4 mb-6">
                <p className="text-brand-800">{blurb}</p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {getPriorityActions().slice(0, 2).map((action, index) => (
                <Button
                  key={index}
                  variant={index === 0 ? "primary" : "secondary"}
                  leftIcon={<action.icon className="h-4 w-4" />}
                  onClick={() => navigate(action.url)}
                  className="w-full sm:w-auto"
                >
                  {action.name}
                </Button>
              ))}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // For system admins, show the administrative dashboard
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <PageHeader
          title={`Welcome back, ${user?.name || 'User'}`}
          description="System Administration Dashboard"
        />
        <Button
          variant="secondary"
          leftIcon={<RefreshCw className="h-4 w-4" />}
          onClick={refresh}
        >
          Refresh Data
        </Button>
      </div>

      {/* Where you are in the year — annual setup workflow */}
      {totalSteps > 0 && (
        <Card className="p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="text-card-title text-ink-900">Where you are in the year</h2>
              <p className="text-body-sm text-ink-500 mt-0.5 font-mono tabular-nums">
                {completedSteps} of {totalSteps} setup steps complete
              </p>
            </div>
            <span className="text-2xl font-bold font-mono tabular-nums text-navy-700">{overallProgress}%</span>
          </div>

          <div className="h-2 rounded-full bg-ink-100 overflow-hidden mb-5">
            <div className="h-full bg-navy-600 rounded-full transition-all" style={{ width: `${overallProgress}%` }} />
          </div>

          {progressStats?.nextStep && (
            <div className="flex items-center justify-between gap-4 rounded-lg border border-navy-200 bg-navy-50 px-4 py-3 mb-5">
              <div>
                <p className="text-meta uppercase tracking-wide text-navy-600 font-semibold">Next step</p>
                <p className="text-body-sm font-medium text-ink-900">{progressStats.nextStep.title}</p>
              </div>
              {progressStats.nextStep.actionUrl && (
                <Button variant="primary" size="sm" onClick={() => navigate(progressStats.nextStep!.actionUrl!)}>
                  {progressStats.nextStep.actionText || 'Open'}
                </Button>
              )}
            </div>
          )}

          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            {setupSteps.map((step) => (
              <li key={step.id} className="flex items-center gap-2 text-body-sm">
                {step.completed ? (
                  <CheckCircle className="h-4 w-4 text-ok-fg flex-shrink-0" />
                ) : (
                  <span className="h-4 w-4 rounded-full border-2 border-ink-300 flex-shrink-0" />
                )}
                <span className={step.completed ? 'text-ink-400 line-through' : 'text-ink-700'}>{step.title.replace(/\s*✓\s*$/, '')}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Key Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-ink-500">Unassigned Classes</p>
              <p className="text-2xl font-bold text-ink-900">{stats?.timetabling?.unassignedClasses || 0}</p>
              <p className="text-xs text-ink-500">
                of {stats?.timetabling?.totalClasses || 0} total classes
              </p>
            </div>
            <div className={`p-3 rounded-full ${
              (stats?.timetabling?.unassignedClasses || 0) > 0 ? 'bg-danger-bg' : 'bg-ok-bg'
            }`}>
              {(stats?.timetabling?.unassignedClasses || 0) > 0 ? (
                <AlertTriangle className="h-6 w-6 text-danger-fg" />
              ) : (
                <CheckCircle className="h-6 w-6 text-ok-fg" />
              )}
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-ink-500">Allocation Progress</p>
              <p className="text-2xl font-bold text-ink-900">{stats?.timetabling?.allocationPercentage || 0}%</p>
              <p className="text-xs text-ink-500">Classes with teachers assigned</p>
            </div>
            <div className={`p-3 rounded-full ${
              (stats?.timetabling?.allocationPercentage || 0) >= 80 ? 'bg-ok-bg' :
              (stats?.timetabling?.allocationPercentage || 0) >= 50 ? 'bg-warn-bg' : 'bg-danger-bg'
            }`}>
              <BarChart3 className={`h-6 w-6 ${
                (stats?.timetabling?.allocationPercentage || 0) >= 80 ? 'text-ok-fg' :
                (stats?.timetabling?.allocationPercentage || 0) >= 50 ? 'text-warn-fg' : 'text-danger-fg'
              }`} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-ink-500">Teaching Intentions</p>
              <p className="text-2xl font-bold text-ink-900">{stats?.intentions?.submissionPercentage || 0}%</p>
              <p className="text-xs text-ink-500">
                {stats?.intentions?.submittedIntentions || 0} of {stats?.intentions?.totalStaff || 0} submitted
              </p>
            </div>
            <div className={`p-3 rounded-full ${
              (stats?.intentions?.submissionPercentage || 0) >= 80 ? 'bg-navy-50' : 'bg-warn-bg'
            }`}>
              <Target className={`h-6 w-6 ${
                (stats?.intentions?.submissionPercentage || 0) >= 80 ? 'text-navy-600' : 'text-warn-fg'
              }`} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-ink-500">Scheduling Conflicts</p>
              <p className="text-2xl font-bold text-ink-900">{stats?.timetabling?.totalConflicts || 0}</p>
              <p className="text-xs text-ink-500">
                {(stats?.timetabling?.totalConflicts || 0) === 0 ? 'No conflicts detected' : 'Cross-grid conflicts'}
              </p>
            </div>
            <div className={`p-3 rounded-full ${
              (stats?.timetabling?.totalConflicts || 0) === 0 ? 'bg-ok-bg' : 'bg-danger-bg'
            }`}>
              {(stats?.timetabling?.totalConflicts || 0) === 0 ? (
                <CheckCircle className="h-6 w-6 text-ok-fg" />
              ) : (
                <AlertTriangle className="h-6 w-6 text-danger-fg" />
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Main Navigation Cards */}
      <div>
        <h3 className="text-lg font-medium text-ink-900 mb-4">Main Functions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Staff Directory Card */}
          <Card
            className="p-6 cursor-pointer hover:shadow-lg transition-all duration-200 border-2 hover:border-navy-300"
            onClick={() => {
              navigate('/staff/directory');
            }}
          >
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-navy-100 rounded-full flex items-center justify-center mb-4">
                <Users className="h-8 w-8 text-navy-600" />
              </div>
              <h4 className="text-lg font-semibold text-ink-900 mb-2">Staff Directory</h4>
              <p className="text-sm text-ink-500 mb-4">
                Manage all staff information, roles, and assignments
              </p>
              <div className="flex items-center justify-center text-navy-600 font-medium">
                <span>Manage Staff</span>
                <ArrowRight className="h-4 w-4 ml-2" />
              </div>
            </div>
          </Card>

          {/* Multi-Grid Editor Card */}
          <Card
            className="p-6 cursor-pointer hover:shadow-lg transition-all duration-200 border-2 hover:border-ok-border"
            onClick={() => {
              navigate('/grid/multi-editor');
            }}
          >
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-ok-bg rounded-full flex items-center justify-center mb-4">
                <Grid3X3 className="h-8 w-8 text-ok-fg" />
              </div>
              <h4 className="text-lg font-semibold text-ink-900 mb-2">Grid Editor</h4>
              <p className="text-sm text-ink-500 mb-4">
                Main timetabling interface for managing class allocations
              </p>
              <div className="flex items-center justify-center text-ok-fg font-medium">
                <span>Edit Timetables</span>
                <ArrowRight className="h-4 w-4 ml-2" />
              </div>
            </div>
          </Card>

          {/* Reports & Analytics Card */}
          <Card
            className="p-6 cursor-pointer hover:shadow-lg transition-all duration-200 border-2 hover:border-navy-300"
            onClick={() => {
              navigate('/reports/preference-satisfaction');
            }}
          >
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-navy-100 rounded-full flex items-center justify-center mb-4">
                <TrendingUp className="h-8 w-8 text-navy-600" />
              </div>
              <h4 className="text-lg font-semibold text-ink-900 mb-2">Reports & Analytics</h4>
              <p className="text-sm text-ink-500 mb-4">
                View system reports and performance analytics
              </p>
              <div className="flex items-center justify-center text-navy-600 font-medium">
                <span>View Reports</span>
                <ArrowRight className="h-4 w-4 ml-2" />
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Priority Alerts (Compact) */}
      {alerts.length > 0 && (
        <Card>
          <div className="p-4 border-b border-ink-200">
            <h3 className="text-lg font-medium text-ink-900">Priority Alerts</h3>
          </div>
          <div className="divide-y divide-ink-200">
            {alerts.slice(0, 3).map(alert => (
              <div key={alert.id} className="p-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    {getAlertIcon(alert.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ink-900">{alert.title}</p>
                    <p className="text-sm text-ink-500 mt-1">{alert.description}</p>
                    {alert.actionUrl && (
                      <button
                        onClick={() => navigate(alert.actionUrl!)}
                        className="mt-2 text-sm text-navy-600 hover:text-navy-800 font-medium inline-flex items-center"
                      >
                        {alert.actionText || 'Take Action'}
                        <ArrowRight className="h-3 w-3 ml-1" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {alerts.length > 3 && (
              <div className="p-4 text-center">
                <p className="text-sm text-ink-500">
                  {alerts.length - 3} more alerts available
                </p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Quick Actions Row */}
      <div>
        <h3 className="text-lg font-medium text-ink-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {getPriorityActions().map((action, index) => (
            <Card
              key={index}
              className="p-4 cursor-pointer hover:shadow-md transition-all"
              onClick={() => navigate(action.url)}
            >
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${action.color} text-white`}>
                  <action.icon className="h-5 w-5" />
                </div>
                <span className="text-sm font-medium text-ink-900">{action.name}</span>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EnhancedDashboard;